import { supabase, Category, CategoryInsert, CategoryUpdate } from '../lib/supabase';
import { StorageService } from './storageService';

export class CategoryService {
  // Get active categories only
  static async getCategories(): Promise<Category[]> {
    try {
      console.log('CategoryService: Fetching categories from Supabase...');
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('CategoryService: Supabase error:', error);
        throw error;
      }
      
      console.log('CategoryService: Successfully fetched active categories:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('CategoryService: Error fetching categories:', error);
      throw error;
    }
  }

  // Get active product categories only (exclude job categories)
  static async getProductCategories(): Promise<Category[]> {
    try {
      console.log('CategoryService: Fetching product categories from Supabase...');
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .eq('is_job_category', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('CategoryService: Supabase error:', error);
        throw error;
      }
      
      console.log('CategoryService: Successfully fetched product categories:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('CategoryService: Error fetching product categories:', error);
      throw error;
    }
  }

  // Get active job categories only
  static async getJobCategories(): Promise<Category[]> {
    try {
      console.log('CategoryService: Fetching job categories from Supabase...');
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true)
        .eq('is_job_category', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('CategoryService: Supabase error:', error);
        throw error;
      }
      
      console.log('CategoryService: Successfully fetched job categories:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('CategoryService: Error fetching job categories:', error);
      throw error;
    }
  }

  // Get all categories (including inactive ones) - for admin purposes
  static async getAllCategories(): Promise<Category[]> {
    try {
      console.log('CategoryService: Fetching all categories from Supabase...');
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('CategoryService: Supabase error:', error);
        throw error;
      }
      
      console.log('CategoryService: Successfully fetched all categories:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('CategoryService: Error fetching all categories:', error);
      throw error;
    }
  }

  // Get a single category by ID
  static async getCategoryById(id: string): Promise<Category | null> {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  }

  // Create a new category
  static async createCategory(category: CategoryInsert): Promise<Category> {
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert(category)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  // Create a new category with image file upload
  static async createCategoryWithImage(
    categoryData: Omit<CategoryInsert, 'image_url'>,
    imageFile?: File
  ): Promise<Category> {
    try {
      // First create the category
      const { data: category, error: categoryError } = await supabase
        .from('categories')
        .insert(categoryData)
        .select()
        .single();

      if (categoryError) throw categoryError;

      // If image file is provided, upload it
      if (imageFile) {
        const imageUrl = await StorageService.uploadCategoryImage(category.id, imageFile);
        if (imageUrl) {
          // Update category with image URL
          const { data: updatedCategory, error: updateError } = await supabase
            .from('categories')
            .update({ image_url: imageUrl })
            .eq('id', category.id)
            .select()
            .single();

          if (updateError) throw updateError;
          return updatedCategory;
        }
      }

      return category;
    } catch (error) {
      console.error('Error creating category with image:', error);
      throw error;
    }
  }

  // Update a category
  static async updateCategory(id: string, updates: CategoryUpdate): Promise<Category> {
    try {
      const { data, error } = await supabase
        .from('categories')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  // Update a category with image file upload
  static async updateCategoryWithImage(
    id: string,
    updates: Omit<CategoryUpdate, 'image_url'>,
    imageFile?: File,
    imageUrl?: string
  ): Promise<Category> {
    try {
      // Get current category to check for existing image
      const { data: currentCategory } = await supabase
        .from('categories')
        .select('image_url')
        .eq('id', id)
        .single();

      let finalUpdates: CategoryUpdate = { ...updates };

      // If new image file is provided, upload it
      if (imageFile) {
        // Delete old image if it exists and is from our storage
        if (currentCategory?.image_url && StorageService.isStorageUrl(currentCategory.image_url)) {
          await StorageService.deleteCategoryImage(currentCategory.image_url);
        }

        // Upload new image
        const newImageUrl = await StorageService.uploadCategoryImage(id, imageFile);
        if (newImageUrl) {
          finalUpdates.image_url = newImageUrl;
        }
      } else if (imageUrl !== undefined) {
        // If imageUrl is provided (including empty string to remove image)
        finalUpdates.image_url = imageUrl;
      }

      // Update category
      const { data, error } = await supabase
        .from('categories')
        .update({
          ...finalUpdates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating category with image:', error);
      throw error;
    }
  }

  // Delete a category
  static async deleteCategory(id: string): Promise<void> {
    try {
      // Get category to check for associated image
      const { data: category } = await supabase
        .from('categories')
        .select('image_url')
        .eq('id', id)
        .single();

      // Delete associated image if it exists and is from our storage
      if (category?.image_url && StorageService.isStorageUrl(category.image_url)) {
        await StorageService.deleteCategoryImage(category.image_url);
      }

      // Delete category
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }

  // Toggle category active status
  static async toggleCategoryStatus(id: string, isActive: boolean): Promise<Category> {
    try {
      const { data, error } = await supabase
        .from('categories')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error toggling category status:', error);
      throw error;
    }
  }

  // Update product count for a category
  static async updateProductCount(id: string, productCount: number): Promise<Category> {
    try {
      const { data, error } = await supabase
        .from('categories')
        .update({
          product_count: productCount,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating product count:', error);
      throw error;
    }
  }
}
